import type { SyntheticEvent } from 'react';
import { memo, useCallback, useId, useState } from 'react';
import { List } from '@mui/material';
import { NestedDropdownWrapper, SubMenuItem, SubMenuText, SubTextBadgeGroup, SubmenuCollapse, SubmenuCollapseInner } from './DropdownMenuItem.styles';
import { MenuItem } from './MenuItem';
import { MenuBadge } from './MenuItem.styles';
import type { IconNames } from '../Icon';
import { buildModulePath, buildAgendaPath } from 'utils/navigation';
import type { Module } from 'types/modules';
import { resolveAgendaName } from 'utils/resolveAgendaName';
import { useBreadcrumbs } from 'contexts/BreadcrumbsProvider';
import { useMenuNavigation } from './useMenuNavigation';
import { toTestIdSegment } from 'utils/testId';
import { buildAgendaNotificationsPath } from 'utils/hooks';
import { isPathActive } from './isPathActive';

interface DropdownMenuItemProps {
  open?: boolean;
  text: string;
  icon?: IconNames;
  links?: {
    path: string;
    name: string;
    unreadNotificationsCount?: number;
  }[];
  path: string;
  openWhenClick?: boolean;
  ownModuleId?: string;
  isNested?: boolean;
  childModules?: Omit<Module, 'childs'>[];
  unreadNotificationsCount?: number;
  dataTestId?: string;
  /** Current pathname — provided by parent to avoid per-item useLocation subscriptions. */
  pathname: string;
  /** Precomputed active state for this item's path. */
  active: boolean;
}

export const DropdownMenuItem = memo(
  ({
    open = false,
    text,
    icon,
    links,
    path,
    openWhenClick,
    ownModuleId,
    isNested = false,
    childModules,
    unreadNotificationsCount,
    dataTestId,
    pathname,
    active,
  }: DropdownMenuItemProps) => {
    const navigateFromMenu = useMenuNavigation();
    const [userOpen, setUserOpen] = useState<boolean | null>(null);
    const [seenPathname, setSeenPathname] = useState(pathname);
    const { clearExtras } = useBreadcrumbs();
    const collapseId = useId();

    // Fix for arrow is desynchronized with agenda list
    if (pathname !== seenPathname) {
      setSeenPathname(pathname);
      setUserOpen(null);
    }

    const dropdownOpen = userOpen ?? active;

    const toggleDropdownOpen = useCallback(() => {
      setUserOpen((prev) => !(prev ?? active));
    }, [active]);

    const handleNavigate = useCallback(
      (link: string) => {
        clearExtras();
        navigateFromMenu(link);
      },
      [clearExtras, navigateFromMenu]
    );

    const openAndNavigateHandler = useCallback(() => {
      if (openWhenClick) {
        setUserOpen(true);
        handleNavigate(path);
      } else {
        toggleDropdownOpen();
      }
    }, [openWhenClick, handleNavigate, path, toggleDropdownOpen]);

    const subMenuClickHandler = useCallback(
      (event: SyntheticEvent, link: string) => {
        event.stopPropagation();
        (event.currentTarget as HTMLElement).blur();
        handleNavigate(link);
      },
      [handleNavigate]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            if (!dropdownOpen) {
              event.preventDefault();
              setUserOpen(true);
            }
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            if (dropdownOpen) {
              event.preventDefault();
              setUserOpen(false);
            }
            break;
          default:
            break;
        }
      },
      [dropdownOpen]
    );

    return (
      <>
        <MenuItem
          active={active}
          open={open}
          dropdownOpen={dropdownOpen}
          icon={icon}
          text={text}
          onClick={openAndNavigateHandler}
          onArrowClick={toggleDropdownOpen}
          onKeyDown={handleKeyDown}
          ariaExpanded={open ? dropdownOpen : undefined}
          ariaControls={open ? collapseId : undefined}
          ariaHasPopup={open ? 'true' : undefined}
          unreadNotificationsCount={unreadNotificationsCount}
          dataTestId={dataTestId ?? `user-menu-item-${toTestIdSegment(text)}`}
          arrow
          nested={isNested}
        />
        {open && (
          <SubmenuCollapse id={collapseId} open={dropdownOpen}>
            <SubmenuCollapseInner>
              <List component="section" disablePadding>
                {links?.map((link) => {
                  const linkActive = isPathActive(pathname, link.path);
                  return (
                    <SubMenuItem
                      key={link.path}
                      active={linkActive}
                      nested={isNested}
                      aria-current={linkActive ? 'page' : undefined}
                      onClick={(event) => subMenuClickHandler(event, link.path)}
                      data-testid={`user-menu-subitem-${toTestIdSegment(link.path)}`}>
                      <SubTextBadgeGroup>
                        <SubMenuText active={linkActive}>{link.name}</SubMenuText>
                        {link.unreadNotificationsCount !== undefined && link.unreadNotificationsCount > 0 && (
                          <MenuBadge
                            data-testid={`user-menu-subitem-${toTestIdSegment(link.path)}-badge`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigate(buildAgendaNotificationsPath(link.path));
                            }}>
                            {link.unreadNotificationsCount}
                          </MenuBadge>
                        )}
                      </SubTextBadgeGroup>
                    </SubMenuItem>
                  );
                })}
                {childModules &&
                  childModules.length > 0 &&
                  childModules.map(
                    (childModule) =>
                      childModule.agendas.length > 0 && (
                        <NestedDropdownWrapper key={childModule.moduleId}>
                          <DropdownMenuItem
                            text={childModule.name}
                            open={open}
                            icon={childModule.icon as IconNames}
                            path={buildModulePath(childModule.moduleId, ownModuleId)}
                            dataTestId={`sidebar-user-module-${toTestIdSegment(childModule.moduleId)}`}
                            openWhenClick
                            ownModuleId={ownModuleId}
                            isNested
                            unreadNotificationsCount={childModule.unreadNotificationsCount}
                            pathname={pathname}
                            active={isPathActive(pathname, buildModulePath(childModule.moduleId, ownModuleId))}
                            links={childModule.agendas
                              .filter((agenda) => !agenda.hideInOverview)
                              .map((agenda) => ({
                                name: resolveAgendaName(agenda),
                                path: buildAgendaPath(childModule.moduleId, agenda, ownModuleId),
                                unreadNotificationsCount: agenda.unreadNotificationsCount,
                              }))}
                          />
                        </NestedDropdownWrapper>
                      )
                  )}
              </List>
            </SubmenuCollapseInner>
          </SubmenuCollapse>
        )}
      </>
    );
  }
);
